import { AuthdUserProfile } from '@/components/AuthdUserProfile'
import LoadingSpinner from '@/components/LoadingSpinner'
import Modal from '@/components/Modal'
import ActivateDevice from '@/components/auth/ActivateDevice'
import {
  AuthBtn,
  BackgroundImageContainer,
  CheckoutBar,
  CheckoutBarBtn,
  CheckoutBarBtnText,
  CheckoutBarSummary,
  CheckoutBarSummaryTitle,
  CheckoutBarSummaryTitleText1,
  CheckoutBarSummaryTitleText2,
  CityStateValue,
  ContactSupportLink,
  DisclaimerFooter,
  FormContainer,
  HavingTrouble,
  MainContent,
  Page,
  RegionLockedMessage,
  SubscribeHeading,
  TopBar,
  ZipCityStateLabelWrapper,
  ZipCodeEntryContainer,
  ZipCodeEntryLabel,
  ZipCodeInput,
  ZipCodeInputWrapper,
  ZipCodeMsg,
} from '@/components/packages/Packages.styles'
import PackagesFaq from '@/components/packages/PackagesFaq'
import PromoStories from '@/components/packages/PromoStories'
import { VerifyCodeResult } from '@/components/types'
import { useSendSelectedPackageAnalytics } from '@/hooks/useSendSelectedPackageAnalytics'
import { useSendViewedPackagesAnalytics } from '@/hooks/useSendViewedPackagesAnalytics'
import { MultiStageAuthContext, PkgSelectionContext, SelectedPkg } from '@/pages/_app'
import { HeadingLabel } from '@/styles/fonts'
import { GrandstandLogo } from '@grandstand-web/bally-web-shared/src/components/GrandstandLogo'
import { usePurchasingDisabled } from '@grandstand-web/bally-web-shared/src/hooks/usePurchasingDisabled'
import { ApiClient } from '@grandstand-web/bally-web-shared/src/newPackages/ApiClient'
import { S, getString } from '@grandstand-web/bally-web-shared/src/newPackages/StringMgmt'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import {
  ConfigServiceContext,
  useContextUnconditionally,
} from '@grandstand-web/bally-web-shared/src/services/config/ConfigService'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import {
  ga4ItemVariantForSelectedPkg,
  resolvePackageResponse,
} from '@grandstand-web/bally-web-shared/src/utils/packageUtils'
import {
  findPackageDataByServiceId,
  getAllPackagesFromPage,
} from '@grandstand-web/bally-web-shared/src/utils/presentationModels/PackageObjectV2'
import { USDCurrency, isValidZipcode } from '@grandstand-web/bally-web-shared/src/utils/stringUtils'
import { PackageSelector, mapPackagesData } from '@grandstand-web/ui'
import { PackagesPageV2 } from '@grandstand/presentation-models'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

type HeadingNormalType = React.PropsWithChildren<{
  text: string
}>

const HeadingNormal = ({ text }: HeadingNormalType) => {
  return (
    <>
      <SubscribeHeading fontSize="4" mobileFontSize="3">
        {text}
      </SubscribeHeading>
    </>
  )
}

const HeadingNoPkgsFound = () => {
  return (
    <>
      <SubscribeHeading fontSize="4" mobileFontSize="3">
        FanDuel Sports Network is not currently available in your area.
      </SubscribeHeading>
    </>
  )
}

type FlowType = 'web' | 'tv' | 'ios' | 'credit'

const determineFlow = (query: ParsedUrlQuery): FlowType => {
  const flow = query.flow
  if (flow === undefined) {
    return 'web'
  }
  let queryString = ''
  if (typeof flow !== 'string') {
    queryString = flow[0] ?? 'web'
  } else {
    queryString = flow
  }

  switch (queryString) {
    case 'ios':
      return 'ios' as FlowType
    case 'credit':
      return 'credit' as FlowType
    case 'tv':
      return 'tv' as FlowType
    default:
      return 'web' as FlowType
  }
}

const Packages = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const flow = determineFlow(router.query)
  const { currentConfig } = useContextUnconditionally(ConfigServiceContext)
  const userService = useContext(UserServiceContext)
  const analyticService = useContext(AnalyticServiceContext)
  const [selectedZip, setSelectedZip] = useState<string | undefined>()
  const [zipCityState, setZipCityState] = useState<string | undefined>()
  const [pkgsInZip, setPkgsInZip] = useState<PackagesPageV2>()
  const [isCodeActivated, setIsCodeActivated] = useState(false)
  const { selectedPkg, setSelectedPkg, setSelectedPackageFromPackageData } = useContext(PkgSelectionContext)
  const { verifiedCode, setVerifiedCode, setPartnerSource } = useContext(MultiStageAuthContext)
  const zipEntryRef = useRef<HTMLInputElement>(null)
  const { isPurchasingDisabled, setIsPurchasingDisabled } = usePurchasingDisabled()
  const isCreditFlow = flow === 'credit'
  const isTVFlow = flow === 'tv'
  const [checkoutUrl, setCheckoutUrl] = useState<string>(() => {
    const existingRedirectUrl = searchParams.get('redirect_url')
    const checkoutPath = isCreditFlow ? '/purchase' : '/checkout'
    if (userService.isLoggedIn) {
      if (existingRedirectUrl) {
        return `${checkoutPath}?redirect_url=${encodeURIComponent(existingRedirectUrl)}`
      }
      return checkoutPath
    }
    if (existingRedirectUrl) {
      return `/signin?redirect_url=${encodeURIComponent(
        `${checkoutPath}?redirect_url=${encodeURIComponent(existingRedirectUrl)}`
      )}`
    }
    return `/signin?redirect_url=${encodeURIComponent(checkoutPath)}`
  })
  const sendViewedPackagesAnalytics = useSendViewedPackagesAnalytics()
  const sendPackageSelectionAnalytics = useSendSelectedPackageAnalytics()

  /**
   * Every package will have only one promotion, either a free trial or some other default promo but never both.
   * Default promos are always applied by default. If the user enters a promo code on check out, it will always override the default promo.
   */
  const defaultPromoOrFreeTrial = selectedPkg?.promotions?.at(0)

  const iosRedirectUrl = flow === 'ios' ? 'ballysports://dtc' : searchParams.get('dtc_purchase_redirect')

  useEffect(() => {
    if (userService.isLoggedIn || userService.currentUser?.profile?.region?.zip_code) {
      setSelectedZip(userService.currentUser?.profile?.region.zip_code)
    } else {
      setSelectedZip(userService.deviceRegion?.zip_code)
    }
  }, [userService.currentUser?.profile?.region.zip_code, userService.deviceRegion?.zip_code, userService.isLoggedIn])

  useEffect(() => {
    setCheckoutUrl((old) => {
      // Partner source will only ever come from non-ios purchases
      if (iosRedirectUrl && !old.includes('?dtc_purchase_redirect')) {
        return `${old}?dtc_purchase_redirect=${iosRedirectUrl}`
      }
      return old
    })
  }, [iosRedirectUrl])

  useEffect(() => {
    if (verifiedCode) {
      setCheckoutUrl((old) => {
        // The "checkout url" may either be directly to /checkout or to /signin with a redirect_url param
        // If it's the latter, then we have to urlencode the question mark for the redirect_url param
        const qm = old.includes('redirect_url') ? '%3F' : '?'
        return `${old}${qm}device_code=${verifiedCode}`
      })
    }
  }, [verifiedCode, isCreditFlow])

  useEffect(() => {
    let cancelStateUpdate = false
    const getPkgs = async () => {
      if (selectedZip === undefined || !isValidZipcode(selectedZip)) {
        return
      }
      const regionUrl = `${currentConfig.API.services.auth_services.region}/${selectedZip}`
      const regionData = await ApiClient.convenientApiFetch({ url: regionUrl, method: 'GET' })
      const { city, state } = await regionData.json()
      setZipCityState(city ? `${city}, ${state}` : '')

      const url = `${currentConfig.API.services.subscription_services.packages_v2}/${selectedZip}`
      const data = await ApiClient.convenientApiFetch({ url: url, method: 'GET' })
      const pkgs = await resolvePackageResponse(data, isPurchasingDisabled, setIsPurchasingDisabled)
      // const pkgs: PackagesPageV2 = await data.json()
      if (!cancelStateUpdate) {
        if (selectedPkg && userService.isLoggedIn && userService.currentUser?.profile.region.is_locked) {
          // see if the user selected an package from a different zip
          if (selectedPkg.package_zipcode !== userService.currentUser?.profile.region.zip_code && setSelectedPkg) {
            // can't select this package, clear it
            setSelectedPkg(undefined)
          }
        }

        if ('status' in pkgs && 'code' in pkgs && 'message' in pkgs) {
          setSelectedZip(undefined)
          setPkgsInZip(undefined)
          if (setSelectedPkg) {
            setSelectedPkg(undefined)
          }

          analyticService.unableToDetectLocation('Unable to detect location')
        } else {
          setPkgsInZip(pkgs)

          if (!userService.isInMarket) {
            analyticService.outOfMarket()
          }
        }
      }
    }
    getPkgs()

    return () => {
      cancelStateUpdate = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfig, selectedZip, setSelectedPkg, userService.isInMarket, analyticService, userService.freeTrialStatus])

  useEffect(() => {
    if (
      pkgsInZip &&
      selectedPkg &&
      userService.isLoggedIn &&
      userService.currentUser?.profile.region.is_locked &&
      setSelectedPkg
    ) {
      const selectedPkgAlreadyPurchased = pkgsInZip.purchased_packages.find((purchased) => {
        const purchaseOptions = purchased.purchase_options
        return (
          purchaseOptions?.annual?.service_id === selectedPkg.service_id ||
          purchaseOptions?.monthly?.service_id === selectedPkg.service_id ||
          purchaseOptions?.seasonal?.service_id === selectedPkg.service_id
        )
      })
      if (selectedPkgAlreadyPurchased) {
        setSelectedPkg(undefined)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPkg?.service_id, userService.isLoggedIn, userService.currentUser?.profile.region.is_locked, pkgsInZip])

  const handleActivateCode = (verifyCodeResult: VerifyCodeResult) => {
    setVerifiedCode?.(verifyCodeResult.code)
    if (verifyCodeResult.partner_source) {
      setPartnerSource?.(verifyCodeResult.partner_source)
    }
    setIsCodeActivated(true)
  }
  const handleCreditCode = async (verifyCodeResult: VerifyCodeResult) => {
    await ensurePurchasingAccount()
    const code = verifyCodeResult.code
    const nextCheckoutUrl = `/purchase?device_code=${code}`
    setVerifiedCode?.(verifyCodeResult.code)
    if (verifyCodeResult.partner_source) {
      setPartnerSource?.(verifyCodeResult.partner_source)
    }
    router.push(nextCheckoutUrl)
  }

  const IosRedirectErrorDialog = () => {
    // We should never reach this error state, because the ios app should always send a redirect url.
    // But including it here for last ditch effort so the user is not stuck here if they somehow manage to reach this state.
    const button = [
      {
        label: 'OK',
        onClick: () => {
          router.push('/support')
        },
      },
    ]
    const title = 'It looks like your iOS device did not provide a redirect url.'
    const msg = 'Please contact support to resolve this issue.'
    return <Modal title={title} msg={msg} buttons={button} canDismiss={false} />
  }

  const addToCartAnalytics = () => {
    // Add To Cart Analytics
    analyticService.addToCart({
      value: userService.freeTrialStatus ? 0 : (selectedPkg?.price ?? 0),
      promotion_name: 'fanduel sports network watch modal',
      package_rsn: selectedPkg?.dtc_product_ids.join('|') ?? '',
      subscription_type: userService.freeTrialStatus ? 'trial' : 'paid',
      // package_price: userService.freeTrialStatus ? 0 : selectedPkg?.price ?? 0,
      package_price: selectedPkg?.price ?? 0,
      package_zipcode: selectedPkg?.package_zipcode ?? '',
      message: selectedPkg?.promotions?.[0]?.display_text ?? '',
      ecommerce: {
        value: userService.freeTrialStatus ? 0 : (selectedPkg?.price ?? 0),
        currency: 'USD',
        items: [
          {
            item_name: selectedPkg?.package_name.toLowerCase() ?? '',
            item_id: selectedPkg?.service_id.toLowerCase().replace('_', ' ') ?? '',
            item_brand: 'fanduel sports network',
            item_category: userService.freeTrialStatus ? 'trial' : 'paid',
            item_variant: ga4ItemVariantForSelectedPkg(selectedPkg),
            quantity: 1,
            coupon: '',
            price: selectedPkg?.price ?? 0,
          },
        ],
      },
    })
  }

  const ensurePurchasingAccount = useCallback(async () => {
    // This call to fetch the prorated bill also implicitly creates the Evergent account if one does not exist.
    // The purpose of call is to account for Evergent's inability to prevent multiple instances of an account to be created in their system.
    // When for example, multiple requests are sent due to re-rendering on the checkout page.
    const url = currentConfig.API.services.subscription_services.bill
    if (url) {
      await ApiClient.convenientApiFetch({
        url,
        method: 'POST',
        body: {
          selected_packages: [
            {
              service_type: 'PACKAGE',
              service_id: 'FL_MONTHLY',
              quantity: 1,
            },
          ],
          coupons: [],
          address: {
            zip_code: '33101',
          },
        },
      }).catch((_) => {
        // don't care, is best effort
      })
    }
  }, [currentConfig.API.services.subscription_services.bill])

  const CheckoutCTA = ({
    userIsLoggedIn,
    userHasFreeTrial,
    defaultPromo,
    selectedPkg,
  }: {
    userIsLoggedIn: boolean
    userHasFreeTrial: boolean
    defaultPromo?: {
      is_default_promotion: boolean
      is_free_trial: boolean
      promotion_type: string
      promotion_category: string
      promotion_price: number
      promotion_id: string
      description?: string | undefined
      display_text: string
      period: string
      duration: number
      percentage?: number | undefined
    }
    selectedPkg: SelectedPkg
  }) => {
    /**
     * A package can only have a single default promo applied at any one time.
     */

    // The selected package has a free trial default promo AND the user is eligible for a promo, or the user is anonymous.
    if (defaultPromo?.is_default_promotion && defaultPromo?.is_free_trial && (userHasFreeTrial || !userIsLoggedIn)) {
      return (
        <CheckoutBar>
          <CheckoutBarSummary>
            <CheckoutBarSummaryTitle>
              <CheckoutBarSummaryTitleText1 fontSize="1">
                Amount due:{' '}
                {`${defaultPromo.description}. Then ${USDCurrency.format(selectedPkg.price)}/${
                  selectedPkg.bill_cycle === 'monthly' ? 'mo' : selectedPkg.bill_cycle === 'annual' ? 'yr' : 'season'
                }`}
              </CheckoutBarSummaryTitleText1>
              <CheckoutBarSummaryTitleText2 fontSize="xs">
                Free trial for new subscribers only.{' '}
                <Link
                  href={
                    getString('subscriber_agreement.url') ??
                    'https://help.fanduelsportsnetwork.com/Answer/Detail/000001450'
                  }
                  target="_blank"
                >
                  Terms apply
                </Link>
                .
              </CheckoutBarSummaryTitleText2>
            </CheckoutBarSummaryTitle>
            <CheckoutBarBtn
              onClick={async () => {
                addToCartAnalytics()
                await ensurePurchasingAccount()
                router.push(checkoutUrl)
              }}
            >
              <CheckoutBarBtnText fontSize="sm">Start free trial</CheckoutBarBtnText>
            </CheckoutBarBtn>
          </CheckoutBarSummary>
        </CheckoutBar>
      )
    }
    // The selected package has a non-free trial default promo added e.g. 25% off AND the user is eligible for free trial (free trial eligibility also determines default promo eligibility)
    // or the user is annonymous
    else if (
      defaultPromo?.is_default_promotion &&
      !defaultPromo?.is_free_trial &&
      (userHasFreeTrial || !userIsLoggedIn)
    ) {
      return (
        <CheckoutBar>
          <CheckoutBarSummary>
            <CheckoutBarSummaryTitle>
              <CheckoutBarSummaryTitleText1 fontSize="1">
                Amount due:{' '}
                {`${defaultPromo?.description}. Then ${USDCurrency.format(selectedPkg.price)}/${
                  selectedPkg.bill_cycle === 'monthly' ? 'mo' : selectedPkg.bill_cycle === 'annual' ? 'yr' : 'season'
                }`}
              </CheckoutBarSummaryTitleText1>
            </CheckoutBarSummaryTitle>
            <CheckoutBarBtn
              onClick={async () => {
                addToCartAnalytics()
                await ensurePurchasingAccount()
                router.push(checkoutUrl)
              }}
            >
              <CheckoutBarBtnText fontSize="sm">Subscribe</CheckoutBarBtnText>
            </CheckoutBarBtn>
          </CheckoutBarSummary>
        </CheckoutBar>
      )
    }
    // The selected package has no default promos
    else {
      return (
        <CheckoutBar>
          <CheckoutBarSummary>
            <CheckoutBarSummaryTitle>
              <CheckoutBarSummaryTitleText1 fontSize="1">
                Amount due:{' '}
                {`${USDCurrency.format(selectedPkg.price)}/${
                  selectedPkg.bill_cycle === 'monthly' ? 'mo' : selectedPkg.bill_cycle === 'annual' ? 'yr' : 'season'
                }`}
              </CheckoutBarSummaryTitleText1>
            </CheckoutBarSummaryTitle>
            <CheckoutBarBtn
              onClick={async () => {
                addToCartAnalytics()
                await ensurePurchasingAccount()
                router.push(checkoutUrl)
              }}
            >
              <CheckoutBarBtnText fontSize="sm">Subscribe</CheckoutBarBtnText>
            </CheckoutBarBtn>
          </CheckoutBarSummary>
        </CheckoutBar>
      )
    }
  }

  const renderPackagesTabs = () => {
    if (!pkgsInZip || !selectedZip || !setSelectedPackageFromPackageData) return null
    // Map the packages data to the PackageSelector props
    const { rawPackageGroups, ...packageSelectorProps } = mapPackagesData(pkgsInZip, {
      disablePromos: !userService.freeTrialStatus,
    })
    // Default to the last tab
    const activePackageGroup = rawPackageGroups.length - 1
    // Send analytics for the active package group on load
    sendViewedPackagesAnalytics(rawPackageGroups[activePackageGroup], selectedZip, userService.freeTrialStatus)

    return (
      <PackageSelector
        {...packageSelectorProps}
        defaultActivePackageGroup={activePackageGroup}
        onPackageGroupSelect={(index) => {
          sendViewedPackagesAnalytics(rawPackageGroups[index], selectedZip, userService.freeTrialStatus)
        }}
        billingOptionSelectedId={selectedPkg?.service_id}
        onBillingOptionSelect={(serviceId) => {
          const packagesLocale = pkgsInZip
            ? `${pkgsInZip.city}, ${pkgsInZip.state}`
            : `${userService.currentUser?.profile.region.city}, ${userService.currentUser?.profile.region.state}`
          const selectedPkgData = findPackageDataByServiceId(getAllPackagesFromPage(pkgsInZip), serviceId)
          if (selectedPkgData?.package) {
            setSelectedPackageFromPackageData(selectedPkgData, selectedZip, packagesLocale)
            sendPackageSelectionAnalytics({
              packageData: selectedPkgData,
              userIsPromoEligible: userService.freeTrialStatus,
              packagesZipcode: selectedZip,
            })
          }
        }}
        onWatchNow={() => {
          router.push('/')
        }}
      />
    )
  }

  if (isPurchasingDisabled) {
    // should be here if dtc kill switch is enabled, redirect to home
    router.replace('/')
    return <></>
  }

  return (
    <Page>
      <TopBar>
        <Link href="/">
          <GrandstandLogo width="100px" logoStyle="horiz" />
        </Link>
        {userService.isLoggedIn ? (
          <AuthdUserProfile name={userService.currentUser?.profile.internal?.name} />
        ) : (
          <Link href="/signin?redirect_url=/packages">
            <AuthBtn buttonSize="sm" buttonStyle="primary">
              SIGN IN
            </AuthBtn>
          </Link>
        )}
      </TopBar>
      {userService.currentUser?.profile.region.is_locked && (
        <RegionLockedMessage>
          <div>
            Your current home zip code cannot be changed, if you need to change your location please{' '}
            <ContactSupportLink
              href={`${
                getString('bally_sports_help_update_zip_code.url') ??
                'https://help.fanduelsportsnetwork.com/Answer/Detail/000001369'
              }`}
              target="_blank"
            >
              contact customer support
            </ContactSupportLink>
            .
          </div>
        </RegionLockedMessage>
      )}
      {isCreditFlow && !isCodeActivated ? (
        <FormContainer data-theme="light">
          <ActivateDevice onCodeActivated={handleCreditCode} deviceCode={searchParams.get('code') ?? undefined} />
        </FormContainer>
      ) : (
        <>
          <MainContent>
            <BackgroundImageContainer>
              {isTVFlow && !isCodeActivated ? (
                <FormContainer data-theme="light">
                  <ActivateDevice
                    onCodeActivated={isCreditFlow ? handleCreditCode : handleActivateCode}
                    deviceCode={searchParams.get('code') ?? undefined}
                  />
                </FormContainer>
              ) : (
                <>
                  {selectedZip ? <HeadingNormal text={getString('packages.header') ?? ''} /> : <HeadingNoPkgsFound />}
                  <ZipCodeEntryContainer>
                    <ZipCodeInputWrapper>
                      <ZipCodeEntryLabel fontSize="min">Zip Code</ZipCodeEntryLabel>
                      <ZipCodeInput
                        placeholder={selectedZip ?? ''}
                        minLength={5}
                        maxLength={5}
                        ref={zipEntryRef}
                        disabled={userService.currentUser?.profile.region.is_locked}
                        onKeyDown={(e) => {
                          const key = e.key
                          // allow numbers, arrow keys, backspace key, copy, paste, and select all commands only
                          if (
                            key === 'ArrowLeft' ||
                            key === 'ArrowRight' ||
                            key === 'ArrowUp' ||
                            key === 'ArrowDown' ||
                            key === 'Backspace'
                          ) {
                            return
                          }
                          if ((e.ctrlKey || e.metaKey) && key === 'a') {
                            return
                          }
                          if ((e.ctrlKey || e.metaKey) && key === 'c') {
                            return
                          }
                          if ((e.ctrlKey || e.metaKey) && key === 'v') {
                            return
                          }
                          if (/[0-9]/.test(key)) {
                            return
                          }
                          e.preventDefault()
                        }}
                        onChange={(e) => {
                          const {
                            target: { value },
                          } = e
                          if (setSelectedPkg) {
                            setSelectedPkg(undefined)
                          }
                          setPkgsInZip(undefined)
                          setSelectedZip(value)
                          userService.updateZipcode(value)
                        }}
                      />
                    </ZipCodeInputWrapper>
                    <ZipCityStateLabelWrapper>
                      <ZipCodeEntryLabel fontSize="min">City, State</ZipCodeEntryLabel>
                      {pkgsInZip && <CityStateValue fontSize="md">{zipCityState}</CityStateValue>}
                    </ZipCityStateLabelWrapper>
                  </ZipCodeEntryContainer>
                  <ZipCodeMsg fontSize="1">
                    This zip code is your your <b>Home Zip Code</b>, which determines what content is available for you
                    to stream. Your first stream must occur in your Home Zip Code. Please refer to our FAQs for more
                    info.
                  </ZipCodeMsg>
                  {flow === 'ios' && !iosRedirectUrl && <IosRedirectErrorDialog />}
                  {pkgsInZip && selectedZip && <>{renderPackagesTabs()}</>}
                  {selectedPkg && (
                    <CheckoutCTA
                      userIsLoggedIn={userService.isLoggedIn}
                      userHasFreeTrial={userService.freeTrialStatus}
                      defaultPromo={defaultPromoOrFreeTrial}
                      selectedPkg={selectedPkg}
                    />
                  )}
                </>
              )}
              {!pkgsInZip && selectedZip && <LoadingSpinner />}
            </BackgroundImageContainer>
            {pkgsInZip && (
              <DisclaimerFooter>
                <S stringKey="dtc.free_trial.disclaimer" />
              </DisclaimerFooter>
            )}
            <PromoStories />
            <PackagesFaq />
            <HavingTrouble>
              <HeadingLabel fontSize="4" mobileFontSize="3">
                <S stringKey="packages.having_trouble" />
              </HeadingLabel>
              <S as="p" stringKey="packages.having_trouble.copy" />
            </HavingTrouble>
          </MainContent>
        </>
      )}
    </Page>
  )
}
export default Packages
